import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  LogOut,
  BookOpen,
  Loader2,
  Briefcase,
  Building2,
  MapPin,
  Clock,
  Users,
  TrendingUp,
  X,
  ChevronRight,
  ExternalLink,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

const CHART_COLORS = ['#65a30d', '#84cc16', '#a3e635', '#bef264', '#d9f99d', '#ecfccb', '#4d7c0f', '#3f6212'];

// Custom tooltip with click hint
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
        <p className="font-medium text-slate-900">{label}</p>
        <p className="text-lime-600 font-semibold">{payload[0].value} candidates</p>
        <p className="text-xs text-slate-400 mt-1">Click to view list →</p>
      </div>
    );
  }
  return null;
};

// Clickable Horizontal Bar Chart Component
const ClickableBarChart = ({ data, title, icon: Icon, color = "#65a30d", filterType, onBarClick }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-lg font-heading flex items-center gap-2">
        <Icon className="h-5 w-5 text-lime-600" />
        {title}
        <Badge variant="secondary" className="ml-auto text-xs">Click bars to drill down</Badge>
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={data} 
            layout="vertical" 
            margin={{ left: 100, right: 20 }}
            onClick={(e) => {
              if (e && e.activeLabel) {
                onBarClick(filterType, e.activeLabel);
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
            <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
            <YAxis 
              type="category" 
              dataKey="name" 
              tick={{ fontSize: 12 }} 
              width={100}
              tickFormatter={(value) => value.length > 15 ? `${value.substring(0, 15)}...` : value}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="count" 
              fill={color} 
              radius={[0, 4, 4, 0]}
              className="cursor-pointer hover:opacity-80 transition-opacity"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </CardContent>
  </Card>
);

// Clickable Donut Chart Component
const ClickableDonutChart = ({ data, title, icon: Icon, filterType, onSliceClick }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-lg font-heading flex items-center gap-2">
        <Icon className="h-5 w-5 text-lime-600" />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={2}
              dataKey="count"
              nameKey="name"
              label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
              onClick={(entry) => {
                if (entry && entry.name) {
                  onSliceClick(filterType, entry.name);
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              {data.map((entry, index) => (
                <Cell 
                  key={entry.name} 
                  fill={CHART_COLORS[index % CHART_COLORS.length]}
                  className="hover:opacity-80 transition-opacity cursor-pointer"
                />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value, name) => [
                <span key="value">{value} candidates<br/><span className="text-xs text-slate-400">Click to view list</span></span>,
                name
              ]}
            />
            <Legend 
              layout="vertical" 
              align="right" 
              verticalAlign="middle"
              onClick={(e) => onSliceClick(filterType, e.value)}
              formatter={(value) => (
                <span className="text-sm text-slate-600 cursor-pointer hover:text-lime-600 transition-colors">{value}</span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </CardContent>
  </Card>
);

// Quick Insights Card - Enhanced Design
const QuickInsightsCard = ({ analytics }) => {
  const insights = [
    { 
      label: 'Top Function', 
      value: analytics?.top_function || 'N/A',
      icon: Briefcase,
      color: 'lime'
    },
    { 
      label: 'Top Industry', 
      value: analytics?.top_industry || 'N/A',
      icon: Building2,
      color: 'blue'
    },
    { 
      label: 'Remote Interest', 
      value: `${analytics?.remote_percentage || 0}%`,
      icon: MapPin,
      color: 'purple',
      isPercent: true
    },
    { 
      label: 'Prefer Equity', 
      value: `${analytics?.equity_preference_percentage || 0}%`,
      icon: TrendingUp,
      color: 'amber',
      isPercent: true
    },
  ];

  const getColorClasses = (color) => {
    const colors = {
      lime: 'bg-lime-50 text-lime-600 border-lime-200',
      blue: 'bg-blue-50 text-blue-600 border-blue-200',
      purple: 'bg-purple-50 text-purple-600 border-purple-200',
      amber: 'bg-amber-50 text-amber-600 border-amber-200'
    };
    return colors[color] || colors.lime;
  };

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-heading flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-lime-600" />
          Key Insights
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {insights.map((insight, index) => {
          const Icon = insight.icon;
          return (
            <div 
              key={index} 
              className={`p-3 rounded-xl border ${getColorClasses(insight.color)} transition-all hover:shadow-sm`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  <span className="text-sm font-medium text-slate-600">{insight.label}</span>
                </div>
                <span className={`font-semibold ${insight.isPercent ? 'text-lg' : 'text-sm'}`}>
                  {insight.value}
                </span>
              </div>
              {insight.isPercent && (
                <div className="mt-2 h-1.5 bg-white/50 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: `${parseInt(insight.value) || 0}%`,
                      backgroundColor: insight.color === 'purple' ? '#9333ea' : insight.color === 'amber' ? '#d97706' : '#65a30d'
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

// Drill-down Modal Component
const DrillDownModal = ({ isOpen, onClose, filterType, filterValue, authAxios }) => {
  const [candidates, setCandidates] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const loadCandidates = useCallback(async () => {
    if (!filterType || !filterValue) return;
    
    setLoading(true);
    try {
      const response = await authAxios.get(`/admin/analytics/drill-down`, {
        params: {
          filter_type: filterType,
          filter_value: filterValue,
          limit: 20
        }
      });
      setCandidates(response.data.candidates || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load drill-down:', error);
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  }, [authAxios, filterType, filterValue]);

  useEffect(() => {
    if (isOpen) {
      loadCandidates();
    }
  }, [isOpen, loadCandidates]);

  const getFilterLabel = () => {
    const labels = {
      function: 'Function',
      industry: 'Industry',
      geography: 'Geography',
      company_size: 'Company Size',
      experience: 'Experience',
      status: 'Status'
    };
    return labels[filterType] || filterType;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ONBOARDING_COMPLETE':
        return <Badge className="bg-lime-100 text-lime-700">Completed</Badge>;
      case 'ONBOARDING_IN_PROGRESS':
        return <Badge className="bg-amber-100 text-amber-700">In Progress</Badge>;
      case 'EMAIL_VERIFIED_PENDING_ONBOARDING':
        return <Badge className="bg-blue-100 text-blue-700">Pending</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-slate-500 text-sm font-normal">{getFilterLabel()}:</span>
            <span className="text-lime-600">{filterValue}</span>
            <Badge variant="secondary" className="ml-2">{total} candidates</Badge>
          </DialogTitle>
        </DialogHeader>
        
        {/* Quick Action: Message this segment */}
        <div className="bg-lime-50 border border-lime-200 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-lime-600" />
            <span className="text-sm text-slate-700">Send a targeted message to this segment</span>
          </div>
          <Button
            size="sm"
            onClick={() => {
              onClose();
              navigate(`/admin/messages?compose=true&targetType=SEGMENT&segmentFilterType=${filterType}&segmentFilterValue=${encodeURIComponent(filterValue)}`);
            }}
            className="bg-evergreen-950 hover:bg-evergreen-900"
          >
            <Mail className="h-4 w-4 mr-2" />
            Message Segment
          </Button>
        </div>
        
        <div className="flex-1 overflow-y-auto mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-lime-600" />
            </div>
          ) : candidates.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No candidates found
            </div>
          ) : (
            <div className="space-y-2">
              {candidates.map((candidate) => (
                <div 
                  key={candidate.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                  onClick={() => {
                    onClose();
                    navigate(`/admin/candidates/${candidate.id}`);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-evergreen-950 to-evergreen-800 flex items-center justify-center text-white text-sm font-medium">
                      {candidate.email.substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {candidate.profile?.first_name && candidate.profile?.last_name
                          ? `${candidate.profile.first_name} ${candidate.profile.last_name}`
                          : candidate.email.split('@')[0]}
                      </p>
                      <p className="text-sm text-slate-500">{candidate.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(candidate.candidate_status)}
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                      <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-lime-500 rounded-full"
                          style={{ width: `${candidate.onboarding_completion_percent || 0}%` }}
                        />
                      </div>
                      <span className="text-xs">{candidate.onboarding_completion_percent || 0}%</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {total > 20 && (
          <div className="border-t border-slate-200 pt-4 mt-4">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                onClose();
                // Navigate to candidates page with filter
                if (filterType === 'status') {
                  const statusMap = {
                    'Completed': 'ONBOARDING_COMPLETE',
                    'In Progress': 'ONBOARDING_IN_PROGRESS',
                    'Pending': 'EMAIL_VERIFIED_PENDING_ONBOARDING'
                  };
                  navigate(`/admin/candidates?status=${statusMap[filterValue] || filterValue}`);
                } else {
                  navigate(`/admin/candidates`);
                }
              }}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View All {total} Candidates
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function AdminAnalytics() {
  const { authAxios, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  
  // Drill-down state
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [drillDownFilter, setDrillDownFilter] = useState({ type: '', value: '' });

  useEffect(() => {
    const loadAnalytics = async () => {
      setLoading(true);
      try {
        const response = await authAxios.get('/admin/dashboard/analytics');
        setAnalytics(response.data);
      } catch (error) {
        console.error('Failed to load analytics:', error);
        toast.error('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    loadAnalytics();
  }, [authAxios]);

  const handleChartClick = (filterType, filterValue) => {
    setDrillDownFilter({ type: filterType, value: filterValue });
    setDrillDownOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-lime-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to="/" className="text-xl font-heading text-slate-900">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
              </Link>
              <nav className="hidden md:flex items-center gap-1">
                <Link to="/admin" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Overview
                </Link>
                <Link to="/admin/candidates" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Candidates
                </Link>
                <Link to="/admin/job-analytics" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Job Market
                </Link>
                <Link to="/admin/analytics" className="px-4 py-2 text-sm font-medium text-lime-700 bg-lime-50 rounded-lg">
                  Analytics
                </Link>
                <Link to="/admin/market-pulse" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Market Pulse
                </Link>
                <Link to="/admin/messages" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Messages
                </Link>
                <Link to="/admin/support" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  User Support
                </Link>
                <Link to="/admin/job-import" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Job Import
                </Link>
              </nav>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/admin/guide">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Admin Guide
                </Button>
              </Link>
              <Link to="/brand" target="_blank">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600">
                  Brand Guide
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={logout}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-heading text-slate-900">Candidate Analytics</h1>
          <p className="text-slate-500">Demographics and preference breakdowns of your candidate database. <span className="text-lime-600 font-medium">Click any chart element to drill down.</span></p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-evergreen-950 to-evergreen-900 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Total Candidates</p>
                  <p className="text-4xl font-heading mt-1">{analytics?.total_candidates || 0}</p>
                </div>
                <Users className="h-10 w-10 text-lime-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Complete Profiles</p>
                  <p className="text-4xl font-heading mt-1 text-slate-900">{analytics?.complete_profiles || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-lime-100 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-lime-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Avg Completion</p>
                  <p className="text-4xl font-heading mt-1 text-slate-900">{analytics?.avg_completion || 0}%</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">This Month</p>
                  <p className="text-4xl font-heading mt-1 text-slate-900">{analytics?.new_this_month || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Users className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 1: Functions & Industries */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ClickableBarChart 
            data={analytics?.by_function || []}
            title="Candidates by Function Interest"
            icon={Briefcase}
            color="#65a30d"
            filterType="function"
            onBarClick={handleChartClick}
          />
          <ClickableBarChart 
            data={analytics?.by_industry || []}
            title="Candidates by Industry Interest"
            icon={Building2}
            color="#3b82f6"
            filterType="industry"
            onBarClick={handleChartClick}
          />
        </div>

        {/* Charts Row 2: Experience & Geography */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ClickableDonutChart 
            data={analytics?.by_experience || []}
            title="By Experience Level"
            icon={Clock}
            filterType="experience"
            onSliceClick={handleChartClick}
          />
          <ClickableBarChart 
            data={analytics?.by_geography || []}
            title="By Geographic Preference"
            icon={MapPin}
            color="#8b5cf6"
            filterType="geography"
            onBarClick={handleChartClick}
          />
        </div>

        {/* Charts Row 3: Company Size & Status */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <ClickableDonutChart 
            data={analytics?.by_company_size || []}
            title="Company Size Preference"
            icon={Building2}
            filterType="company_size"
            onSliceClick={handleChartClick}
          />
          <ClickableDonutChart 
            data={analytics?.by_status || []}
            title="Onboarding Status"
            icon={Users}
            filterType="status"
            onSliceClick={handleChartClick}
          />
          <QuickInsightsCard analytics={analytics} />
        </div>
      </main>

      {/* Drill-down Modal */}
      <DrillDownModal
        isOpen={drillDownOpen}
        onClose={() => setDrillDownOpen(false)}
        filterType={drillDownFilter.type}
        filterValue={drillDownFilter.value}
        authAxios={authAxios}
      />
    </div>
  );
}
